import { Component, HostListener, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserService } from '../../../../core/services/user/user.service';
import { RolesService } from '../../../../core/services/user/roles.service';
import { OrganizationService } from '../../../../core/services/organization/organization.service';
import { ToastService } from '../../../../core/services/toast.service';
import { CurrentUserService } from '../../../../core/services/auth/current-user.service';
import { StatusUtils } from '../../../../utils/status-utils';
import { ModalService } from '../../../shared/services/modal.service';
import { CreateUserComponent } from '../create-user/create-user.component';
import { EditUserComponent } from '../edit-user/edit-user.component';
import { FacilityService } from '../../../../core/services/organization/facility.service';
import { FacilitySimpleItem } from '../../../../core/models/organization/facility.model';
import { Organization } from 'app/core/models/organization/organization.model';
import { ListColumnConfig } from 'app/main/components/list-page-config/list-page.config';
import { UserListModel, UserStatus } from 'app/core/models/user/user-list.model';
import { ListCardItem } from 'app/main/components/list-card/list-card.component';
import { GetUserListRequest, GetUserListResponse } from 'app/core/models/user/user.model';
import { PermissionService } from 'app/core/services/user/permission.service';
import { DynamicListConfig } from 'app/core/models/table/dynamic-list.model';
import { DynamicListComponent } from 'app/shared/components/dynamic-list/dynamic-list.component';
import { PageSizeSelectorComponent } from 'app/shared/components/page-size-selector/page-size-selector.component';
import { HasClaimDirective } from 'app/shared/directives/has-claim.directive';
import { BaseListComponent } from 'app/shared/base/base-list.component';
import { ListStateStackService } from 'app/core/services/list/list-state-stack.service';

@Component({
  selector: 'app-user-list-demo',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    DynamicListComponent,
    PageSizeSelectorComponent,
    HasClaimDirective,
  ],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss',
  providers: [DatePipe],
})
export class UserListComponent extends BaseListComponent implements OnInit, OnDestroy, AfterViewInit {
  constructor(
    private userService: UserService,
    private rolesService: RolesService,
    private organizationService: OrganizationService,
    private translate: TranslateService,
    private toastService: ToastService,
    private modalService: ModalService,
    public permissionService: PermissionService,
    private currentUserService: CurrentUserService,
    private facilityService: FacilityService,
    listStateStack: ListStateStackService,
  ) {
    super(listStateStack, 'user-list');
    this.collapseElementId = 'collapseExample'; // Set the collapse element ID for this list page
    const userLang = localStorage.getItem('app-lang') || 'en-US';
    this.translate.use(userLang);

    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];
  }

  users: UserListModel[] = [];
  userCards: ListCardItem[] = [];

  totalUser = 0;
  totalPage = 0;
  pageSizes = [10, 15, 25, 50, 100];
  pageHeight!: number;
  userListLoading = true;
  deleteModalId = '';
  userStatus!: UserStatus[];

  override filterForm = {
    nameSurname: '',
    role: [] as number[],
    email: '',
    pid: '',
    organizationId: [] as string[],
    facilityId: [] as string[],
    sortBy: null as string | null,
    sortDirection: null as 'asc' | 'desc' | null,
  };

  roles: any[] = [];
  organizations: Organization[] = [];
  facilities: FacilitySimpleItem[] = [];
  isSystemAdmin = false;
  isFacilityAdmin = false;
  maxDate: string;

  userConfig: DynamicListConfig = {
    pageTitleKey: 'userList',
    columns: [
      {
        key: 'name',
        labelKey: 'NAME',
        type: 'user',
        width: 'minmax(280px, 2.2fr)',
      },
      {
        key: 'userOrganizations',
        labelKey: 'ORGANIZATION',
        type: 'text',
        width: 'minmax(150px, 1fr)',
        transform: (val: any[]) =>
          val && val.length > 0 ? val[0].organizationName : '-',
      },
      {
        key: 'roleName',
        labelKey: 'ROLE',
        type: 'text',
        width: 'minmax(120px, 1fr)',
      },
      {
        key: 'email',
        labelKey: 'EMAIL',
        type: 'text',
        width: 'minmax(150px, 1.3fr)',
      },

      {
        key: 'lastLoginDate',
        labelKey: 'LAST LOGIN',
        type: 'date',
        width: 'minmax(120px, 1fr)',
      },
      {
        key: 'userStatus',
        labelKey: 'STATUS',
        type: 'text',
        width: 'minmax(150px, 1fr)',
        // cssClass: (item) => StatusUtils.getUserStatusClass(item.userStatus),
        transform: (val) => StatusUtils.getUserStatusText(val),
      },
      {
        key: 'actions',
        labelKey: 'ACTIONS',
        type: 'actions',
        width: 'minmax(140px, 0.8fr)',
      },
    ],
    actions: [
      {
        icon: 'fa-regular fa-pen-to-square',
        title: 'edit',
        permission: 'update_user_claim',
        onClick: (item) => this.openEditUserModal(item),
      },
      {
        icon: 'fa-regular fa-trash-can',
        title: 'delete',
        permission: 'delete_user_claim',
        onClick: (item) => this.onDeleteCard(item),
      },
    ],
  };

  activeTooltip: { text: string; x: number; y: number } | null = null;
  private tooltipTimeout: any = null;
  private pendingTooltipPosition: { x: number; y: number } | null = null;

  showTooltip(event: MouseEvent, text: string) {
    if (!text) return;
    const translatedText = this.translate.instant(text);

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    const x = rect.left;
    const y = rect.bottom + 4;

    this.pendingTooltipPosition = { x, y };

    if (this.tooltipTimeout) clearTimeout(this.tooltipTimeout);
    this.tooltipTimeout = setTimeout(() => {
      if (this.pendingTooltipPosition) {
        this.activeTooltip = {
          text: translatedText,
          x: this.pendingTooltipPosition.x,
          y: this.pendingTooltipPosition.y,
        };
      }
    }, 300);
  }

  hideTooltip() {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }
    this.pendingTooltipPosition = null;
    this.activeTooltip = null;
  }

  override ngOnInit(): void {
    super.ngOnInit();
    // Only load data if state wasn't restored
    if (!this.stateWasRestored) {
      this.getUserList(this.currentPage, this.pageSize, this.filterForm);
    }
    this.checkUserRoleAndLoadOrganizations();
    this.getRoles();
  }

  ngAfterViewInit(): void {
    this.restoreCollapseState();
    this.setupCollapseListener();
  }

  private setupCollapseListener(): void {
    if (!this.collapseElementId) return;
    const collapseElement = document.getElementById(this.collapseElementId);
    if (collapseElement) {
      collapseElement.addEventListener('show.bs.collapse', () => this.updateCollapseState(true));
      collapseElement.addEventListener('hide.bs.collapse', () => this.updateCollapseState(false));
    }
  }

  private checkUserRoleAndLoadOrganizations(): void {
    const currentUser = this.currentUserService.GetSession();
    this.isSystemAdmin = this.currentUserService.hasClaim('system_admin_claim');
    this.isFacilityAdmin = this.currentUserService.hasClaim('facility_admin_claim');

    if (this.isSystemAdmin) {
      this.organizationService.getAllOrganizationItems().subscribe({
        next: (orgs) => {
          this.organizations = orgs || [];
        },
        error: (err) => {
          console.error('Error loading organizations:', err);
          this.organizations = [];
        },
      });
    }
  }

  onOrganizationChange(): void {
    this.filterForm.facilityId = [];
    this.facilities = [];

    if (this.filterForm.organizationId.length > 0) {
      const facilityRequests = this.filterForm.organizationId.map((id) =>
        this.facilityService.getAllFacilitiesByOrganization(id),
      );

      import('rxjs').then(({ forkJoin }) => {
        forkJoin(facilityRequests).subscribe({
          next: (responses: any[]) => {
            const allFacilities: FacilitySimpleItem[] = [];
            responses.forEach((res) => {
              if (res.data) {
                allFacilities.push(...res.data);
              }
            });
            this.facilities = allFacilities.filter(
              (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
            );
          },
          error: (err) => {
            console.error('Error loading facilities:', err);
            this.facilities = [];
          },
        });
      });
    }
  }

  openCreateUserModal(event: MouseEvent) {
    const modalRef = this.modalService.openComponentModal(CreateUserComponent, {
      size: 'lg',
      backdrop: true,
      keyboard: true,
    });

    modalRef.closed.subscribe((result) => {
      if (result === 'success') {
        this.getUserList(this.currentPage, this.pageSize, this.filterForm);
      }
    });

    (event.currentTarget as HTMLElement).blur();
  }

  openEditUserModal(user: UserListModel) {
    const modalRef = this.modalService.openComponentModal(EditUserComponent, {
      size: 'lg',
      backdrop: true,
      keyboard: true,
      data: { userId: user.id },
    });

    modalRef.closed.subscribe((result) => {
      if (result === 'success') {
        this.getUserList(this.currentPage, this.pageSize, this.filterForm);
      }
    });
  }

  getRoles(): void {
        this.rolesService.getRoles().subscribe({
      next: (res) => {
        const items = res.data?.items || [];
        this.roles = items
          .map((x: any) => ({
            id: x.role?.id,
            name: x.role?.name,
            roleClaims: x.role?.roleClaims || [],
          }))
          .filter((role: any) => {
            if (this.isFacilityAdmin) {
              const hasOrgAdminClaim = role.roleClaims.some(
                (rc: any) => rc.claim?.name === 'organization_admin_claim',
              );
              if (hasOrgAdminClaim) {
                return false;
              }
            }
            return true;
          })
          .sort((a: any, b: any) => a.name.localeCompare(b.name));
      },
    });
  }

  getOrganizations(): void {
    this.organizationService.getAllOrganizationItems().subscribe({
      next: (res) => {
        this.organizations = res.sort((a, b) => a.name.localeCompare(b.name));
      },
      error: () => {},
    });
  }

  getUserList(page: number, size: number, _filters?: any): void {
    this.userListLoading = true;

    const pageIndex = page > 0 ? page - 1 : 0;

    const request: GetUserListRequest = {
      index: pageIndex,
      size: size,
      Name: this.filterForm.nameSurname || undefined,
      RoleIds:
        this.filterForm.role.length > 0 ? this.filterForm.role : undefined,
      Email: this.filterForm.email || undefined,
      OrganizationIds:
        this.filterForm.organizationId.length > 0
          ? this.filterForm.organizationId
          : undefined,
      FacilityIds:
        this.filterForm.facilityId.length > 0
          ? this.filterForm.facilityId
          : undefined,
      SortBy: this.filterForm.sortBy || undefined,
      SortDirection: this.filterForm.sortDirection || undefined,
    };

    this.userService.getUserList(request).subscribe({
      next: (res: GetUserListResponse) => {
        if (res.data && res.data.items) {
          this.users = res.data.items as any;
          this.totalUser = res.data.count;
          this.totalPage = res.data.pages;

          if (this.filterForm.sortBy && this.filterForm.sortDirection) {
            const direction = this.filterForm.sortDirection === 'asc' ? 1 : -1;
            this.users.sort((a: any, b: any) => {
              let valA, valB;

              switch (this.filterForm.sortBy) {
                case 'name':
                  valA = (a.name || '').toLowerCase();
                  valB = (b.name || '').toLowerCase();
                  break;
                case 'userOrganizations':
                  valA =
                    a.userOrganizations && a.userOrganizations.length > 0
                      ? a.userOrganizations[0].organizationName.toLowerCase()
                      : '';
                  valB =
                    b.userOrganizations && b.userOrganizations.length > 0
                      ? b.userOrganizations[0].organizationName.toLowerCase()
                      : '';
                  break;
                case 'userStatus':
                  valA = a.userStatus;
                  valB = b.userStatus;
                  break;
                default:
                  return 0;
              }

              if (valA < valB) return -1 * direction;
              if (valA > valB) return 1 * direction;
              return 0;
            });
          }

          this.userCards = this.users.map((u) => this.mapToListCardItem(u));

          this.loadAvatarsForUsers();
        } else {
          this.users = [];
          this.userCards = [];
          this.totalUser = 0;
        }

        this.currentPage = page;
        this.userListLoading = false;
      },
      error: (err) => {
        console.error('getUserList error', err);
        this.userListLoading = false;
        this.userCards = [];
      },
    });
  }

  private mapToListCardItem(user: UserListModel): ListCardItem {
    const u: any = user;

    let orgName = '-';

    if (u.userOrganizations && u.userOrganizations.length > 0) {
      orgName = u.userOrganizations[0].organizationName;
    } else if (u.organizationName) {
      orgName = u.organizationName;
    }

    const fullName = u.name ? u.name.trim() : '';

    return {
      id: u.id,
      name: fullName,
      organization: orgName,
      role: u.roleName ?? '',
      email: u.email ?? '',
      lastLoginDate: u.lastLoginDate
        ? u.lastLoginDate
        : (u.createdDateTime ?? ''),
      userStatus: u.userStatus ?? 0,
    };
  }

  filterUserList(e: Event) {
    e.preventDefault();
    this.getUserList(0, this.pageSize, this.filterForm);
  }

  clearFilters(): void {
    this.filterForm = {
      nameSurname: '',
      role: [],
      email: '',
      pid: '',
      organizationId: [],
      facilityId: [],
      sortBy: null,
      sortDirection: null,
    };
    this.facilities = [];

    this.currentPage = 1;
    this.getUserList(1, this.pageSize);
  }

  changePage(page: number): void {
    this.currentPage = page + 1;
    if (this.currentPage === 0 || this.currentPage <= this.totalPage) {
      this.getUserList(this.currentPage, this.pageSize, this.filterForm);
    }
  }

  onSort(column: string): void {
    if (this.filterForm.sortBy === column) {
      if (this.filterForm.sortDirection === 'asc') {
        this.filterForm.sortDirection = 'desc';
      } else if (this.filterForm.sortDirection === 'desc') {
        this.filterForm.sortBy = null;
        this.filterForm.sortDirection = null;
      }
    } else {
      this.filterForm.sortBy = column;
      this.filterForm.sortDirection = 'asc';
    }
    this.getUserList(this.currentPage, this.pageSize, this.filterForm);
  }

  previousPage(page: number) {
    if (this.currentPage > 1) {
      this.getUserList(this.currentPage - 1, this.pageSize, this.filterForm);
    }
  }

  nextPage(page: number) {
    this.getUserList(this.currentPage + 1, this.pageSize, this.filterForm);
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.getUserList(this.currentPage, this.pageSize, this.filterForm);
  }

  onCardClick(card: ListCardItem) {
    if (this.permissionService.hasPermission('update_user_claim')) {
      this.onEdit(card);
    }
  }

  onEdit(card: ListCardItem) {
    const user = this.users.find((u) => u.id === card.id);
    if (user) {
      this.openEditUserModal(user);
    }
  }

  onDeleteCard(card: ListCardItem) {
    this.deleteModalId = String(card.id);
    setTimeout(() => {
      const modalElement = document.getElementById(
        `exampleModal-${this.deleteModalId}`,
      );
      if (modalElement) {
        const modal = new (window as any).bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 0);
  }

  getUserStatusClass(status: number): string {
    return StatusUtils?.getUserStatusClass(status);
  }

  getUserStatusText(status: string): string {
    return StatusUtils?.getUserStatusText(status);
  }

  deleteUser(id: string): void {
    if (id) {
      this.userService.deleteUser(id).subscribe({
        next: () => {
          this.toastService.showToast(
            this.translate.instant('successDeleteUserDesc'),
            'success',
          );
          this.getUserList(0, this.pageSize, this.filterForm);
        },
      });
    }
  }

  getColClass(col: ListColumnConfig): string {
    switch (col.colType) {
      case 'user':
        return 'col-user';
      case 'dataOrg':
        return 'col-data';
      case 'dataEmail':
        return 'col-data-email';
      case 'actions':
        return 'col-actions';
      default:
        return 'col-data';
    }
  }

  toggleRoleSelection(roleId: number) {
    const index = this.filterForm.role.indexOf(roleId);
    if (index > -1) {
      this.filterForm.role.splice(index, 1);
    } else {
      this.filterForm.role.push(roleId);
    }
  }

  isRoleSelected(roleId: number): boolean {
    return this.filterForm.role.includes(roleId);
  }

  getRoleDisplayText(): string {
    if (this.filterForm.role.length === 0) {
      return this.translate.instant('selectRole');
    }
    const firstRoleId = this.filterForm.role[0];
    const role = this.roles.find((r) => r.id === firstRoleId);
    const roleName = role ? role.name : '';

    if (this.filterForm.role.length > 1) {
      return `${roleName} + ${this.filterForm.role.length - 1}`;
    }
    return roleName;
  }

  toggleOrganizationSelection(orgId: string) {
    const index = this.filterForm.organizationId.indexOf(orgId);
    if (index > -1) {
      this.filterForm.organizationId.splice(index, 1);
    } else {
      this.filterForm.organizationId.push(orgId);
    }
    this.onOrganizationChange();
  }

  isOrganizationSelected(orgId: string): boolean {
    return this.filterForm.organizationId.includes(orgId);
  }

  getOrganizationDisplayText(): string {
    if (this.filterForm.organizationId.length === 0) {
      return this.translate.instant('selectOrganization');
    }
    const firstOrgId = this.filterForm.organizationId[0];
    const org = this.organizations.find((o) => o.id === firstOrgId);
    const orgName = org ? org.name : '';

    if (this.filterForm.organizationId.length > 1) {
      return `${orgName} + ${this.filterForm.organizationId.length - 1}`;
    }
    return orgName;
  }

  toggleFacilitySelection(facilityId: string) {
    const index = this.filterForm.facilityId.indexOf(facilityId);
    if (index > -1) {
      this.filterForm.facilityId.splice(index, 1);
    } else {
      this.filterForm.facilityId.push(facilityId);
    }
  }

  isFacilitySelected(facilityId: string): boolean {
    return this.filterForm.facilityId.includes(facilityId);
  }

  getFacilityDisplayText(): string {
    if (this.filterForm.facilityId.length === 0) {
      return this.translate.instant('selectFacility');
    }
    const firstFacilityId = this.filterForm.facilityId[0];
    const facility = this.facilities.find((f) => f.id === firstFacilityId);
    const facilityName = facility ? facility.name : '';

    if (this.filterForm.facilityId.length > 1) {
      return `${facilityName} + ${this.filterForm.facilityId.length - 1}`;
    }
    return facilityName;
  }

  private loadAvatarsForUsers(): void {
    this.users.forEach((user, index) => {
      if (user.id && user.avatarFolderPath) {
        this.userService.getAvatar(user.id).subscribe({
          next: (res) => {
            if (res.isSuccess && res.data?.avatar && this.users[index]) {
              this.compressImage(
                res.data.avatar,
                (compressedBase64: string) => {
                  this.users[index].avatarFolderPath =
                    'data:image/png;base64,' + compressedBase64;
                },
              );
            }
          },
          error: (err: any) => {
            console.debug('Failed to load avatar for user:', user.id);
          },
        });
      }
    });
  }

  private compressImage(
    base64String: string,
    callback: (compressedBase64: string) => void,
  ): void {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const maxSize = 200; // Max dimension in pixels
      let width = img.width;
      let height = img.height;

      // Scale proportionally to fit within 200px
      if (width > height) {
        if (width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
        const compressedBase64 = canvas.toDataURL('image/png').split(',')[1];
        callback(compressedBase64);
      }
    };
    img.src = 'data:image/png;base64,' + base64String;
  }

  override loadData(): void {
    this.getUserList(this.currentPage, this.pageSize, this.filterForm);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
